import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Shield, Mail, Calendar } from 'lucide-react';
import SEO from '../../components/SEO';

const PrivacyPolicyPage = () => {
  const lastUpdated = '17 Aralık 2025';

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <SEO 
        title="Gizlilik Politikası ve KVKK Aydınlatma Metni"
        description="Borsamex gizlilik politikası ve KVKK kapsamında kişisel verilerin işlenmesine ilişkin aydınlatma metni."
        url="/legal/privacy"
      />
      
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
            <Shield className="h-3 w-3 mr-1" /> Yasal
          </Badge>
          <h1 className="text-4xl font-bold text-white mb-4">
            Gizlilik Politikası ve KVKK Aydınlatma Metni
          </h1>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Calendar className="h-4 w-4" />
            <span>Son güncelleme: {lastUpdated}</span>
          </div>
        </div>

        {/* Content */}
        <Card className="border-slate-800 bg-slate-900/50">
          <CardContent className="p-8 prose prose-invert max-w-none">
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Veri Sorumlusu</h2>
              <p className="text-slate-300">
                Bu aydınlatma metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, 
                veri sorumlusu sıfatıyla <strong className="text-white">Datex Bilişim AŞ</strong> ("Şirket") 
                tarafından hazırlanmıştır.
              </p>
              <div className="mt-4 p-4 rounded-lg bg-slate-800/50">
                <p className="text-slate-300 mb-1"><strong className="text-white">Şirket:</strong> Datex Bilişim AŞ</p>
                <p className="text-slate-300 mb-1"><strong className="text-white">Platform:</strong> Borsamex</p>
                <p className="text-slate-300 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <strong className="text-white">İletişim:</strong> info@borsamex.com
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. İşlenen Kişisel Veriler</h2>
              <p className="text-slate-300 mb-4">
                Platform üzerinden aşağıdaki kişisel verileriniz işlenmektedir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li><strong className="text-white">Kimlik Bilgileri:</strong> Ad, soyad</li>
                <li><strong className="text-white">İletişim Bilgileri:</strong> E-posta adresi</li>
                <li><strong className="text-white">Hesap Bilgileri:</strong> Kullanıcı adı, şifre (şifrelenmiş)</li>
                <li><strong className="text-white">İşlem Bilgileri:</strong> Platform üzerindeki aktiviteler, strateji tercihleri</li>
                <li><strong className="text-white">Teknik Veriler:</strong> IP adresi, tarayıcı bilgisi, çerezler</li>
                <li><strong className="text-white">Finansal Bilgiler:</strong> Ödeme bilgileri (ödeme sağlayıcı üzerinden)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Kişisel Verilerin İşlenme Amaçları</h2>
              <p className="text-slate-300 mb-4">
                Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Üyelik işlemlerinin gerçekleştirilmesi ve hesap yönetimi</li>
                <li>Platform hizmetlerinin sunulması</li>
                <li>Strateji ve sinyal hizmetlerinin sağlanması</li>
                <li>Ödeme işlemlerinin gerçekleştirilmesi</li>
                <li>Müşteri destek hizmetlerinin sunulması</li>
                <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                <li>Platform güvenliğinin sağlanması</li>
                <li>İstatistiksel analizler ve hizmet iyileştirmeleri</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Kişisel Verilerin Aktarımı</h2>
              <p className="text-slate-300 mb-4">
                Kişisel verileriniz, yukarıda belirtilen amaçlarla sınırlı olmak üzere:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Yasal zorunluluk halinde yetkili kamu kurum ve kuruluşlarına</li>
                <li>Ödeme işlemleri için ödeme hizmet sağlayıcılarına</li>
                <li>Teknik altyapı hizmetleri için hizmet sağlayıcılarına</li>
                <li>E-posta gönderimi için e-posta servis sağlayıcılarına</li>
              </ul>
              <p className="text-slate-300 mt-4">
                aktarılabilmektedir. Verileriniz yurt dışına aktarılması durumunda, KVKK'nın 9. maddesi 
                kapsamında gerekli önlemler alınmaktadır.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Veri İşlemenin Hukuki Sebepleri</h2>
              <p className="text-slate-300 mb-4">
                Kişisel verileriniz, KVKK'nın 5. ve 6. maddeleri kapsamında aşağıdaki hukuki sebeplere dayanılarak işlenmektedir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Açık rızanızın bulunması</li>
                <li>Sözleşmenin kurulması veya ifası için gerekli olması</li>
                <li>Hukuki yükümlülüğün yerine getirilmesi</li>
                <li>Meşru menfaatlerimiz için zorunlu olması</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Veri Saklama Süresi</h2>
              <p className="text-slate-300">
                Kişisel verileriniz, işleme amaçlarının gerektirdiği süre boyunca ve yasal saklama 
                yükümlülüklerimiz kapsamında saklanmaktadır. Üyelik sona erdikten sonra, yasal 
                zorunluluklar saklı kalmak kaydıyla, verileriniz makul süre içinde silinir veya anonim hale getirilir.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. KVKK Kapsamındaki Haklarınız</h2>
              <p className="text-slate-300 mb-4">
                KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
                <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                <li>İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme</li>
                <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
                <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme</li>
                <li>Düzeltme, silme veya yok etme işlemlerinin aktarıldığı üçüncü kişilere bildirilmesini isteme</li>
                <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
                <li>Kanuna aykırı işleme sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Başvuru Yöntemi</h2>
              <p className="text-slate-300 mb-4">
                Yukarıda belirtilen haklarınızı kullanmak için aşağıdaki yöntemlerle başvurabilirsiniz:
              </p>
              <div className="p-4 rounded-lg bg-slate-800/50">
                <p className="text-slate-300 mb-2">
                  <strong className="text-white">E-posta:</strong> info@borsamex.com
                </p>
                <p className="text-slate-300 text-sm">
                  Başvurunuzda kimliğinizi tespit edici bilgiler ile talebinizin açık ve anlaşılır 
                  şekilde yer almalıdır. Başvurularınız en geç 30 gün içinde sonuçlandırılacaktır.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Değişiklikler</h2>
              <p className="text-slate-300">
                Bu gizlilik politikası, yasal düzenlemeler veya hizmetlerimizdeki değişiklikler 
                doğrultusunda güncellenebilir. Önemli değişiklikler e-posta yoluyla bildirilecektir.
              </p>
            </section>

          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
          <Link to="/legal/terms" className="text-slate-400 hover:text-primary transition-colors">
            Kullanım Koşulları
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/risk" className="text-slate-400 hover:text-primary transition-colors">
            Risk Uyarısı
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/cookies" className="text-slate-400 hover:text-primary transition-colors">
            Çerez Politikası
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
